import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DeadlineDateCalcualator } from '@zaoblako/shared/common/classes/deadline.date.calcualator';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { MasterMilestoneFindInterface } from '@zaoblako/shared/modules/catalog/dtos/master-milestone/master-milestone-find.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { ChecklistTypeFindInterface } from '@zaoblako/shared/modules/project/dto/checklist-type/checklist-type-find.dto';
import type { ChecklistFormInterface } from '@zaoblako/shared/modules/project/dto/checklist/checklist-form.dto';
import { ProjectCreateDto, type ProjectCreateInterface } from '@zaoblako/shared/modules/project/dto/project/project-create.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { SubprojectDefaultsFindInterface } from '@zaoblako/shared/modules/project/dto/subproject-defaults/subproject-defaults-find.dto';
import type { ProjectTemplateFindInterface } from '@zaoblako/shared/modules/project/dto/template/project-template-find.dto';
import { ProjectHelpdeskTasksKindEnum } from '@zaoblako/shared/modules/project/enum/project.helpdesk.tasks.kind.enum';
import { SubsKindEnum } from '@zaoblako/shared/modules/project/enum/subs.kind.enum';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file-find.dto';
import type { BudgetRequestFindInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/budget-request-find.dto';
import { BudgetRequestService } from '@zaoblako/web/core/services/budget/budget-request.service';
import { ClientService } from '@zaoblako/web/core/services/client/client.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { DealService } from '@zaoblako/web/core/services/deal/deal.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FileService } from '@zaoblako/web/core/services/file.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { HelpDeskTaskService } from '@zaoblako/web/core/services/helpdesk/help-desk-task.service';
import { ProjectChecklistService } from '@zaoblako/web/core/services/project/project-checklist.service';
import { ProjectMasterMilestoneService } from '@zaoblako/web/core/services/project/project-master-milestone.service';
import { ProjectTemplateService } from '@zaoblako/web/core/services/project/project-template.service';
import { ProjectService } from '@zaoblako/web/core/services/project/project.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { TemplateEntityVariableService } from '@zaoblako/web/core/services/template-entity-variable.service';
import { SubprojectDefaultsFillModalComponent } from '@zaoblako/web/modules/project/components/project/card/components/subproject-defaults-fill-modal/subproject-defaults-fill-modal.component';
import { ChecklistPeriodFormComponent } from '@zaoblako/web/modules/project/dumbs/checklist-period-form/checklist-period-form.component';
import { FilesListComponent } from '@zaoblako/web/shared/components/files-list/files-list.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { AutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/auto-complete/auto-complete.component';
import { ClientAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/client-auto-complete/client-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { DealMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/deal-multi-select/deal-multi-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { HdTaskMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/hd-task-multi-select/hd-task-multi-select.component';
import { MasterMilestoneSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/master-milestone-single-select/master-milestone-single-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { PrioritySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/priority-single-select/priority-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { DateAddPipe } from '@zaoblako/web/shared/pipes/date-add.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { cloneDeep } from 'lodash';
import moment from 'moment-timezone';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { forkJoin, Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-project-form',
  templateUrl: './project-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    SingleSelectComponent,
    AutoCompleteComponent,
    MultiSelectComponent,
    InputNumberModule,
    EditorComponent,
    FormsModule,
    DatepickerComponent,
    FileUploadModule,
    DividerModule,
    UserMultiSelectComponent,
    DateAddPipe,
    ChecklistPeriodFormComponent,
    FilesListComponent,
    DealMultiSelectComponent,
    DirectionSingleSelectComponent,
    PrioritySingleSelectComponent,
    SpinnerComponent,
    HdTaskMultiSelectComponent,
    UnitSingleSelectComponent,
    FilialSingleSelectComponent,
    MasterMilestoneSingleSelectComponent,
    UserSingleSelectComponent,
    ClientAutoCompleteComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectFormComponent extends BaseFormComponent<ProjectCreateInterface> implements OnInit, OnDestroy {
  public engine: 'class-transformer' | 'serde' = 'serde';

  @Input() public readonly index: number;
  public project = <ProjectCreateInterface>{};
  public dto = ProjectCreateDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  @ViewChild('fileUpload') public fileUpload: FileUpload;
  public uploadingFiles = false;

  public users: UserFindInterface[] = [];
  public userGroups: UserGroupFindInterface[] = [];
  public projects: ProjectFindInterface[] = [];
  public checklistTypes: ChecklistTypeFindInterface[] = [];
  public masterMilestones: MasterMilestoneFindInterface[] = [];
  public directions: DirectionFindInterface[] = [];
  public tasks: HelpdeskTaskFindInterface[] = [];

  public participants: UserFindInterface[] = [];

  public memberInfo: {
    creator: UserFindInterface;
    responsible: UserFindInterface;
    auditorUsers: UserFindInterface[];
  } = {
    creator: null,
    responsible: null,
    auditorUsers: []
  };

  public isCreator: boolean;

  private ref: DynamicDialogRef;

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly projectService: ProjectService,
    private readonly projectTemplateService: ProjectTemplateService,
    private readonly userService: UserService,
    private readonly userGroupService: UserGroupService,
    private readonly projectChecklistService: ProjectChecklistService,
    private readonly projectMasterMilestoneService: ProjectMasterMilestoneService,
    private readonly clientService: ClientService,
    private readonly directionService: DirectionService,
    private readonly budgetRequestService: BudgetRequestService,
    private readonly helpDeskTaskService: HelpDeskTaskService,
    private readonly dealService: DealService,
    private readonly authService: AuthService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly router: Router,
    private readonly dialogService: DialogService,
    private readonly templateEntityVariableService: TemplateEntityVariableService,
    private readonly fileService: FileService
  ) {
    super();
  }

  public ngOnInit() {
    const parentId = this.formStoreService.getState.forms[this.index]?.data?.parentId;
    const templateId = this.formStoreService.getState.forms[this.index]?.data?.templateId;
    const dealId = this.formStoreService.getState.forms[this.index]?.data?.dealId;
    const taskId = this.formStoreService.getState.forms[this.index]?.data?.taskId;
    const budgetingId = this.formStoreService.getState.forms[this.index]?.data?.budgetingId;
    const clientId = this.formStoreService.getState.forms[this.index]?.data?.clientId;

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.checkParentId(parentId);

    if (!isNaN(budgetingId)) {
      this.budgetRequestService.findById<BudgetRequestFindInterface>(budgetingId).subscribe((budget) => {
        this.project.budgetRequests = [budget];
        this.cdRef.markForCheck();
      });
    }

    if (!isNaN(dealId)) {
      this.dealService.findById<DealFindInterface>(dealId).subscribe((deal) => {
        this.project.deals = [deal];
        this.cdRef.markForCheck();
      });
    }

    if (!isNaN(taskId)) {
      this.helpDeskTaskService.findById<HelpdeskTaskFindInterface>(taskId).subscribe((task) => {
        this.project.helpdeskTasks = [task];
        this.cdRef.markForCheck();
      });
    }

    if (!isNaN(clientId)) {
      this.clientService.findById<ClientFindInterface>(clientId).subscribe((client) => {
        this.project.client = client;
        this.cdRef.markForCheck();
      });
    }

    if (!isNaN(templateId)) {
      this.projectTemplateService.findById<ProjectTemplateFindInterface>(templateId).subscribe((template) => {
        const checklists = template.checklists.map((checklist) => {
          return <ChecklistFormInterface>{
            name: this.templateEntityVariableService.parse(checklist.name),
            type: checklist.type,
            masterMilestone: checklist.masterMilestone,
            responsible: checklist.responsible,
            description: this.templateEntityVariableService.parse(checklist.description)
          };
        });

        this.project = {
          name: this.templateEntityVariableService.parse(template.name),
          responsible: template.responsible,
          masterMilestone: template.masterMilestone,
          direction: template.direction,
          unit: template.unit,
          client: template.client,
          parent: template.parentProject,
          deals: template.deals,
          description: this.templateEntityVariableService.parse(template.description),
          userAuditors: template.userAuditors,
          userGroupAuditors: template.userGroupAuditors,
          checklists: checklists,
          template: <ProjectTemplateFindInterface>{ id: templateId },
          deadline: DeadlineDateCalcualator.getInstance().calculateDate(
            template.deadline,
            template.planning.nearestDate || template.planning.runDate || moment().toDate()
          ),
          filial: template.filial,
          priority: template.priority
        };
        this.cdRef.markForCheck();
      });
    }

    forkJoin([
      this.userService.findAll(),
      this.userGroupService.find<UserGroupFindInterface>(),
      this.projectChecklistService.getChecklistTypes(),
      this.projectMasterMilestoneService.find<MasterMilestoneFindInterface>()
    ]).subscribe(([users, userGroups, checklistTypes, masterMilestones]) => {
      this.users = users;
      this.userGroups = userGroups;
      this.checklistTypes = checklistTypes;
      this.masterMilestones = masterMilestones;
      this.cdRef.markForCheck();
    });

    forkJoin([this.directionService.find<DirectionFindInterface>(), this.helpDeskTaskService.search()]).subscribe(
      ([directions, result]) => {
        this.directions = directions;
        this.tasks = result.items;
        this.cdRef.markForCheck();
      }
    );

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public ngOnDestroy() {
    this.ref?.close();
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    forkJoin([
      this.projectService.findById<ProjectFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId),
      this.projectService.getHelpdeskTasks(this.formStoreService.getState.forms[this.index]?.entityId, {
        kind: ProjectHelpdeskTasksKindEnum.ALL
      }),
      this.projectService.getDeals(this.formStoreService.getState.forms[this.index]?.entityId, {
        statusKind: SubsKindEnum.ALL
      })
    ]).subscribe(([project, helpdeskTasks, deals]) => {
      this.project = project as any;
      this.project.helpdeskTasks = helpdeskTasks;
      this.project.deals = deals;
      this.project = { ...this.project };
      this.isCreator = this.authService.currentUser?.id === project.creator?.id;
      this.memberInfo.creator = project.creator;
      this.updateMembers();
      this.formStoreService.setLoading(false, this.index);
      this.cdRef.markForCheck();
    });
  }

  public saveProject() {
    if (this.checkValidate(this.project).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.projectService.update<ProjectCreateInterface, ProjectFindInterface>(this.project).subscribe({
          next: (project) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Задача "${project.name}" обновлена`);
            this.formStoreService.saveNext(project, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.projectService.create<ProjectFindInterface>(this.project).subscribe({
          next: (project) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Задача создана');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/project/card/' + project.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(project, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteProject() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить задачу "${this.project.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.projectService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe(() => {
          this.errorService.addSuccessMessage(`Задача "${this.project.name}" удалена`);
          this.formStoreService.deleteNext(this.index);
        });
      }
    });
  }

  public projectSearch(q: string) {
    this.projectService.getAccessibleProjects({ search: q }).subscribe((projects) => {
      this.projects = projects;
      this.cdRef.markForCheck();
    });
  }

  public changeParentProject() {
    if (this.project.parent?.id) {
      this.projectService.getSubprojectDefaults(this.project.parent.id).subscribe((defaults) => {
        if (this.isValidDefaults(defaults)) {
          this.openModalWithDetails(defaults);
        }
      });
    }
  }

  public updateMembers() {
    this.memberInfo = {
      creator: this.memberInfo.creator,
      responsible: this.project.responsible,
      auditorUsers: []
    };

    if (this.project.userGroupAuditors?.length || this.project.userAuditors?.length) {
      const auditorUsers = [...this.project.userAuditors];
      this.project.userGroupAuditors?.forEach((auditor) => {
        if (auditor.users?.length) {
          auditorUsers.push(...auditor.users);
        }
      });

      this.memberInfo.auditorUsers = auditorUsers;
    }

    const members = [];
    members.push(this.memberInfo?.creator ? this.memberInfo.creator : this.authService.currentUser);
    if (this.memberInfo?.responsible) {
      members.push(this.memberInfo.responsible);
    }
    if (this.memberInfo?.auditorUsers?.length) {
      members.push(...this.memberInfo.auditorUsers);
    }
    this.participants = members.reduce((array: UserFindInterface[], user) => {
      if (array.findIndex((item) => item.id === user.id) === -1) {
        array.push(user);
      }
      return array;
    }, []);
  }

  public addChecklist() {
    if (!this.project.checklists) {
      this.project.checklists = [];
    }

    this.project.checklists.push(<ChecklistFormInterface>{});
  }

  public deleteChecklist(checklist: ChecklistFormInterface) {
    this.project.checklists = this.project.checklists.filter((_checklist) => _checklist !== checklist);
  }

  public uploadFiles(data: { files: FileList }) {
    this.uploadingFiles = true;
    this.fileService.upload(data.files).subscribe({
      next: (files) => {
        this.project.files = this.project.files?.length ? this.project.files.concat(files) : files;
        this.fileUpload.clear();
        this.uploadingFiles = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.uploadingFiles = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public deleteFile(filesArray: FileFindInterface[], file: FileFindInterface) {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить файл "${file.name}"?`,
      accept: () => {
        filesArray.splice(filesArray.indexOf(file), 1);
        this.cdRef.markForCheck();
      }
    });
  }

  public checkParentId(parentId: number) {
    if (!isNaN(parentId)) {
      this.projectService.findById<ProjectFindInterface>(parentId).subscribe((parentProject) => {
        this.project.parent = parentProject;
        this.projects = [parentProject];
        this.cdRef.markForCheck();
      });
      this.projectService.getSubprojectDefaults(parentId).subscribe((defaults) => {
        Object.keys(defaults).forEach((key) => {
          this.project[key] = cloneDeep(defaults[key]);
        });
      });
    }
  }

  private isValidDefaults(defaults: SubprojectDefaultsFindInterface): boolean {
    const countValid = Object.values(defaults).reduce((acc, field) => {
      if (Array.isArray(field)) {
        return field.length ? acc + 1 : acc;
      } else {
        return field !== null ? acc + 1 : acc;
      }
    }, 0);
    return !!countValid;
  }

  private openModalWithDetails(defaults: SubprojectDefaultsFindInterface) {
    this.ref = this.dialogService.open(SubprojectDefaultsFillModalComponent, {
      header: 'Заполнить задачу реквизитами надзадачи?',
      contentStyle: { overflow: 'auto' },
      styleClass: 'xl:w-4 lg:w-6 md:w-8 sm:w-10 w-full',
      data: {
        subprojectDefaults: defaults,
        parentId: this.project.parent.id,
        parentProjectName: this.project.parent.name
      }
    });
    this.ref.onClose.subscribe((parentId: number) => {
      this.checkParentId(parentId);
    });
  }
}
